import type { AiChatInputAttachment } from './ai-chat-input';
import type { AiChatMessage, AiChatAttachment } from './types';

import { useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { useDispatch, useSelector } from 'src/state';
import {
  clearMessages,
  addUserMessage,
  selectCurrentHtml,
  selectSimulationError,
  sendSimulationMessage,
  selectSimulationMessages,
  selectSimulationIsSending,
} from 'src/state/simulation-chat/simulation-chat-slice';

import { Iconify } from 'src/components/iconify';

import { AiChatInput } from './ai-chat-input';
import { TypingIndicator } from './typing-indicator';
import { FireflyAnimation } from './firefly-animation';
import { AiChatMessageList } from './ai-chat-message-list';

// ----------------------------------------------------------------------

export function AiChat() {
  const dispatch = useDispatch();

  const reduxMessages = useSelector(selectSimulationMessages);
  const isLoading = useSelector(selectSimulationIsSending);
  const error = useSelector(selectSimulationError);
  const currentHtml = useSelector(selectCurrentHtml);

  // Convert Redux messages to component format
  const messages: AiChatMessage[] = reduxMessages.map((msg) => ({
    id: msg.id,
    content: msg.content,
    role: msg.role,
    createdAt: msg.createdAt,
    htmlContent: msg.htmlContent,
    attachments: msg.attachments?.map((att) => ({
      id: att.id,
      name: att.name,
      size: att.size,
      type: att.type,
      preview: att.preview,
      base64Data: att.base64Data,
    })),
  }));

  const handleSendMessage = useCallback(
    async (content: string, attachments?: AiChatInputAttachment[]) => {
      // Convert input attachments to message attachments
      const messageAttachments: AiChatAttachment[] | undefined = attachments?.map((att) => ({
        id: att.id,
        name: att.name,
        size: att.size,
        type: att.type,
        preview: att.preview,
        base64Data: att.base64Data,
      }));

      // Add user message to state immediately
      dispatch(
        addUserMessage({
          content,
          attachments: messageAttachments?.map((att) => ({
            id: att.id,
            name: att.name,
            size: att.size,
            type: att.type,
            base64Data: att.base64Data || '',
            preview: att.preview,
          })),
        })
      );

      // Send to API
      dispatch(
        sendSimulationMessage({
          content,
          attachments: attachments?.map((att) => ({
            id: att.id,
            name: att.name,
            size: att.size,
            type: att.type,
            base64Data: att.base64Data || '',
            preview: att.preview,
          })),
        })
      );
    },
    [dispatch]
  );

  const handleClearChat = useCallback(() => {
    dispatch(clearMessages());
  }, [dispatch]);

  const handleDownloadHtml = useCallback(() => {
    if (!currentHtml) return;

    const blob = new Blob([currentHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'simulation.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentHtml]);

  const renderTypingIndicator = () => <TypingIndicator />;

  const renderError = () =>
    error && (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          Error: {error}
        </Typography>
      </Stack>
    );

  const hasMessages = messages.length > 0;

  const renderHtmlPreview = () => (
    <Box
      sx={{
        flex: '2 1 0',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Preview Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.neutral',
        }}
      >
        <Typography variant="subtitle2">Simulation Preview</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Iconify icon="eva:cloud-download-fill" width={18} />}
            onClick={handleDownloadHtml}
            disabled={!currentHtml}
          >
            Download HTML
          </Button>
        </Stack>
      </Stack>

      {/* Preview Content */}
      <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
        {currentHtml ? (
          <iframe
            srcDoc={currentHtml}
            title="Simulation Preview"
            sandbox="allow-scripts allow-same-origin"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        ) : (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <FireflyAnimation />
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderEmptyState = () => (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ flex: '1 1 auto', p: 3, textAlign: 'center' }}
    >
      <Box
        component="img"
        src="/dwyl/en_GB.png"
        alt="Create Interactive Simulations"
        sx={{ width: 840, maxWidth: '100%', height: 'auto', mb: 2, opacity: 0.8 }}
      />
      <Typography variant="h6" sx={{ mb: 1 }}>
        Create Interactive Simulations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mb: 3 }}>
        Upload a PDF or image containing educational content, and I&apos;ll generate an interactive
        simulation for you.
      </Typography>
    </Stack>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
      }}
    >
      {/* Left side - HTML preview */}
      {hasMessages && renderHtmlPreview()}

      {/* Chat area */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: hasMessages ? '1 1 0' : '1 1 auto',
          minWidth: hasMessages ? 0 : undefined,
        }}
      >
        {/* Header with clear button */}
        {hasMessages && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.neutral',
            }}
          >
            <Typography variant="subtitle2">Chat</Typography>
            <IconButton size="small" onClick={handleClearChat} title="Clear chat">
              <Iconify icon="solar:trash-bin-trash-bold" width={20} />
            </IconButton>
          </Stack>
        )}

        {/* Messages area */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flex: hasMessages ? '1 1 auto' : undefined,
          }}
        >
          {hasMessages ? <AiChatMessageList messages={messages} /> : renderEmptyState()}

          {isLoading && renderTypingIndicator()}
          {renderError()}
        </Box>

        {/* Input area */}
        <AiChatInput
          onSendMessage={handleSendMessage}
          disabled={isLoading}
          placeholder="Describe the educational content or ask for modifications..."
        />
      </Box>
    </Box>
  );
}
